import { useRoute, type ModuleScreenContext } from '@huaca/ui';
import { PostingPanel } from './PostingPanel';
import { SearchPage } from './SearchPage';

/**
 * The product's own routing.
 *
 * Hand-rolled against `useRoute()` — there is no `:id` matching in the shared router, and ModuleApp
 * already owns `/account`, `/org`, `/plans`, `/billing/return`, `/auth/*` and `/invite`, so this
 * only has to tell two shapes apart. Anything unrecognised falls back to the search screen rather
 * than a 404: a mistyped tenders URL should land somewhere useful.
 *
 * `renderProduct` is only reached once the caller holds a seat, so nothing here re-checks that.
 */
export function Product({ ctx }: { ctx: ModuleScreenContext }) {
  const { path, search } = useRoute();
  const parts = path.split('/').filter(Boolean);
  const posting = parts[0] === 'postings' && parts[1] && parts[2] ? { slug: parts[1], siteId: parts[2] } : null;

  /**
   * The search is ALWAYS rendered, and a posting is layered over it.
   *
   * Two things follow, both deliberate. It is the same `<SearchPage>` element across both routes,
   * so React keeps it mounted when a posting opens — which is what preserves the list, the facet
   * counts and the scroll position inside both panes, rather than refetching them on the way back.
   *
   * And a cold load — a shared link, a bookmark, a refresh — gets a working search behind the panel
   * instead of a dead end, at the cost of one extra list request on that first paint.
   */
  return (
    <>
      <SearchPage ctx={ctx} />
      {posting && <PostingPanel slug={posting.slug} siteId={posting.siteId} search={search} ctx={ctx} />}
    </>
  );
}
